/**
 * ResearchOverview.jsx — the verdict (HANDOFF §5.2).
 *
 * The page answers one question: is any strategy ready for capital? Everything
 * here is derived from /api/research/summary, so no panel can contradict
 * another. Missing values render as an em dash; empty sections say WHY.
 */

import React from "react";

import { ago, fmtInt, fmtNum, fmtPct } from "../../lib/format";
import { useResearchSummary, useStrategies } from "../../lib/researchApi";
import { AuthorBars, BacktestVsForwardChart, EdgeVsSampleChart } from "./ResearchCharts";

const DASH = "—";

function Card({ title, sub, children, style }) {
  return (
    <section className="rx-card" style={style}>
      {title && (
        <header style={{ marginBottom: 12 }}>
          <div className="rx-label">{title}</div>
          {sub && <div className="rx-faint" style={{ fontSize: 11, marginTop: 3 }}>{sub}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

/* ── 1. Verdict banner ────────────────────────────────────────────────────── */

function Verdict({ verdict, onReview }) {
  if (!verdict) return null;
  return (
    <section className="rx-card" style={{ borderColor: verdict.ready ? "var(--accent)" : "var(--border)" }}>
      <div className="rx-label">Is any strategy ready for capital?</div>
      <div className="rx-display"
           style={{ fontSize: "clamp(34px, 7vw, 56px)", margin: "10px 0 8px",
                    color: verdict.ready ? "var(--accent)" : "var(--text)" }}>
        {verdict.headline}
      </div>
      <p className="rx-muted" style={{ fontSize: 13.5, lineHeight: 1.6, margin: "0 0 16px", maxWidth: "62ch" }}>
        {verdict.reason}
      </p>
      <button type="button" className="rx-btn" onClick={onReview}>Review strategies →</button>
    </section>
  );
}

/* ── 2. System state strip ────────────────────────────────────────────────── */

function StateCell({ label, value, note, tone, warn }) {
  return (
    <div className="rx-cell"
         style={{ flex: "1 1 160px", minWidth: 0,
                  borderColor: warn ? "var(--warn)" : "var(--border)",
                  background: warn ? "rgba(242,184,75,.08)" : "var(--panel-2)" }}>
      <div className="rx-label" style={{ fontSize: 11 }}>{label}</div>
      <div className="rx-num" style={{ fontSize: 17, marginTop: 6, color: tone || "var(--text)" }}>
        {value}
      </div>
      {note && (
        <div className="rx-faint" style={{ fontSize: 10.5, marginTop: 4, lineHeight: 1.45 }}>
          {warn && <span className="rx-tag rx-tag-review" style={{ marginRight: 6 }}>check</span>}
          {note}
        </div>
      )}
    </div>
  );
}

function SystemStrip({ regime, feeds, lab }) {
  const r = regime || {};
  const fresh = (feeds || []).filter((f) => f.status === "fresh").length;
  const actWhilePaused = r.act_mode && r.engines_paused;

  return (
    <div className="rx-row">
      <StateCell
        label="Regime"
        value={r.regime || DASH}
        note={r.entropy_market != null
          ? `market entropy ${fmtNum(r.entropy_market, 3)}${r.trend ? ` · ${r.trend.toLowerCase()} trend` : ""}`
          : "no entropy reading"}
        tone={r.regime ? "var(--text)" : "var(--faint)"}
        warn={r.stale}
      />
      <StateCell
        label="Position sizing"
        value={r.position_multiplier != null ? `×${fmtNum(r.position_multiplier, 2)}` : DASH}
        note={r.engines_paused ? "engine signals PAUSED" : "engines trading"}
        tone={r.engines_paused ? "var(--warn)" : "var(--text)"}
      />
      <StateCell
        label="ACT mode"
        value={r.act_mode == null ? DASH : r.act_mode ? "ON" : "OFF"}
        note={actWhilePaused
          ? "auto-acting while engines are paused"
          : r.act_mode ? "desk auto-acts within bounds" : "actions queue for approval"}
        tone={actWhilePaused ? "var(--warn)" : "var(--text)"}
        warn={actWhilePaused}
      />
      <StateCell
        label="Feeds"
        value={feeds?.length ? `${fresh} of ${feeds.length}` : DASH}
        note={(feeds || [])
          .map((f) => `${f.name} ${f.fetched_at ? ago(f.fetched_at) : "never"}`)
          .join(" · ") || "no feed data"}
        warn={!!feeds?.length && fresh < feeds.length}
      />
      <StateCell
        label="Lab"
        value={lab?.generation != null ? `gen ${fmtInt(lab.generation)}` : DASH}
        note={lab?.cycle != null
          ? `cycle ${fmtInt(lab.cycle)}${lab.running ? " · running" : " · idle"}`
          : "no cycle data"}
      />
    </div>
  );
}

/* ── 3. Funnel ────────────────────────────────────────────────────────────── */

const FUNNEL_STEPS = [
  ["ideas_evaluated", "Ideas evaluated"],
  ["traded", "Traded"],
  ["positive_after_fees", "Positive after fees"],
  ["promoted", "Promoted"],
  ["forward", "Forward-tested"],
  ["approved", "Approved"],
];

function Funnel({ funnel }) {
  if (!funnel) return null;
  const values = FUNNEL_STEPS.map(([k]) => funnel[k]);
  const max = Math.max(...values.filter((v) => v != null), 1);

  return (
    <div className="rx-row" style={{ gap: 10 }}>
      {FUNNEL_STEPS.map(([key, label], i) => {
        const v = funnel[key];
        const prev = i > 0 ? values[i - 1] : null;
        const pct = prev != null && v != null && prev > 0 ? (v / prev) * 100 : null;
        // Log-scaled width so 64,749 and 47 can share a row legibly.
        const w = v == null ? 0 : Math.max(3, (Math.log10(Math.max(1, v) + 1) / Math.log10(max + 1)) * 100);
        const zero = v === 0;
        return (
          <div key={key} className="rx-cell" style={{ flex: "1 1 150px", minWidth: 0 }}>
            <div className="rx-label" style={{ fontSize: 11 }}>{label}</div>
            <div className="rx-num" style={{ fontSize: 22, marginTop: 6,
                                             color: zero ? "var(--neg)" : "var(--text)" }}>
              {v == null ? DASH : fmtInt(v)}
            </div>
            <div style={{ height: 6, background: "var(--row-line)", borderRadius: 3,
                          marginTop: 8, overflow: "hidden" }}>
              <div style={{ width: `${w}%`, height: "100%", borderRadius: 3,
                            background: zero ? "var(--neg)" : "var(--accent)" }} />
            </div>
            <div className="rx-faint rx-num" style={{ fontSize: 10.5, marginTop: 5 }}>
              {pct == null ? (i === 0 ? "start" : "—") : `${pct.toFixed(pct < 10 ? 1 : 0)}% of previous`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── 7. Needs attention ───────────────────────────────────────────────────── */

function Attention({ items }) {
  if (!items?.length) {
    return (
      <div className="rx-empty">
        Nothing needs attention: no stale or disagreeing regime sources, no
        unresolved duplicates, and no strategy flagged for audit.
      </div>
    );
  }
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {items.map((a, i) => (
        <li key={i} style={{ display: "flex", gap: 10, padding: "10px 0",
                             borderBottom: i < items.length - 1 ? "1px solid var(--row-line)" : "none" }}>
          <span className={a.severity === "fix_now" ? "rx-tag rx-tag-fix" : "rx-tag rx-tag-review"}
                style={{ flexShrink: 0, marginTop: 1 }}>
            {a.severity === "fix_now" ? "Fix now" : "Review"}
          </span>
          <span style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--text-2)" }}>{a.text}</span>
        </li>
      ))}
    </ul>
  );
}

/* ── 8. The deploy bar ────────────────────────────────────────────────────── */

function DeployBar({ bar }) {
  if (!bar) return null;
  const gates = [
    ["Sample", `at least ${fmtInt(bar.min_trades)} backtest trades`],
    ["Edge", "net return per trade is positive after fees"],
    ["Breadth", `at least ${bar.min_trades_per_pair} trades per profitable pair`],
    ["Forward sample", `at least ${bar.min_forward_trades} forward-test trades`],
    ["Holds up", `forward edge is at least ${Math.round((bar.holdup_ratio || 0) * 100)}% of the backtest edge`],
    ["Unique", "one record per strategy, no unresolved duplicates"],
  ];
  return (
    <div>
      {gates.map(([name, rule]) => (
        <div key={name} style={{ display: "flex", gap: 10, padding: "8px 0",
                                 borderBottom: "1px solid var(--row-line)" }}>
          <span aria-hidden="true"
                style={{ marginTop: 7, flexShrink: 0, width: 4, height: 4,
                         borderRadius: 2, background: "var(--faint)" }} />
          <span style={{ fontSize: 12.5, lineHeight: 1.5 }}>
            <strong style={{ color: "var(--text)" }}>{name}</strong>
            <span className="rx-muted"> — {rule}</span>
          </span>
        </div>
      ))}
      <div className="rx-faint" style={{ fontSize: 11.5, marginTop: 12, lineHeight: 1.55 }}>
        <strong style={{ color: "var(--warn)" }}>Dropped: win rate ≥ 50%.</strong> The
        forward-test champion wins 73.5% of its trades and still lost 2.77% of its
        book — its losers are far bigger than its winners. Win rate is reported,
        not gated.
      </div>
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────────── */

export default function ResearchOverview({ onGoToStrategies }) {
  const { data, error, loading, updatedAt } = useResearchSummary();
  // The charts need the records themselves, not just the counts.
  // Every scored strategy, not just the promoted ones: promoted now implies
  // >= 100 trades, so a promoted-only scatter would leave the "too few trades"
  // zone permanently empty and the chart would make no point at all.
  const { data: allData } = useStrategies();

  if (loading) {
    return <div className="rx-empty">Loading the research summary…</div>;
  }
  if (error && !data) {
    return (
      <div className="rx-empty">
        The research summary could not be loaded ({error}). No numbers are shown
        rather than stale ones.
      </div>
    );
  }
  if (!data?.ok) {
    return <div className="rx-empty">The research summary returned an error: {data?.error || "unknown"}.</div>;
  }

  const all = allData?.items || [];
  const promoted = all.filter((r) => r.stage === "promoted");

  return (
    <div className="rx-grid" style={{ gap: 14 }}>
      {error && (
        <div className="rx-empty" style={{ borderColor: "var(--warn)", color: "var(--warn)" }}>
          Showing the last good data — the most recent refresh failed ({error}).
        </div>
      )}

      <Verdict verdict={data.verdict} onReview={onGoToStrategies} />

      <SystemStrip regime={data.regime} feeds={data.feeds} lab={data.lab} />

      <Card title="The funnel" sub={data.regime?.sentence}>
        <Funnel funnel={data.funnel} />
      </Card>

      <div className="rx-grid rx-grid-2-1">
        <Card title="Edge vs sample size"
              sub="Every scored strategy. A high return on a small sample is the shape of luck, not edge.">
          <EdgeVsSampleChart records={all} />
        </Card>
        <div className="rx-grid" style={{ gap: 14 }}>
          <Card title="Where promoted strategies come from">
            <AuthorBars records={promoted} />
          </Card>
        </div>
      </div>

      <div className="rx-grid rx-grid-3">
        <Card title="Backtest vs forward">
          <BacktestVsForwardChart records={promoted}
                                  minForward={data.deploy_bar?.min_forward_trades || 30} />
        </Card>
        <Card title="Needs attention">
          <Attention items={data.attention} />
        </Card>
        <Card title="One deploy bar" sub="Every strategy is judged on these six gates.">
          <DeployBar bar={data.deploy_bar} />
        </Card>
      </div>

      <div className="rx-faint" style={{ fontSize: 11, textAlign: "right" }}>
        {data.generated_at ? `Server data ${ago(data.generated_at)}` : ""}
        {updatedAt ? ` · fetched ${ago(new Date(updatedAt).toISOString())}` : ""}
      </div>
    </div>
  );
}
